import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useSession } from './hooks/useSession';
import { useQueueTriggers } from './hooks/useQueue';
import { applyThemeMode, loadThemeMode, type ThemeMode } from './lib/theme';
import { AppShell } from './components/AppShell';
import { CaptureRoute } from './routes/Capture';
import { ResultRoute } from './routes/Result';
import { MyListRoute } from './routes/MyList';
import { SearchRoute } from './routes/Search';
import { DetailRoute } from './routes/Detail';
import { DashboardRoute } from './routes/Dashboard';
import { AdminRoute } from './routes/Admin';
import { AdminUsersRoute } from './routes/AdminUsers';
import { ChangePasswordRoute, LoginRoute } from './routes/Login';
import type { SessionResponse } from './lib/api';
import type { Role } from '../shared/types';
import { ROLE_RANK } from '../shared/types';

export function App() {
  const { state, reload } = useSession();
  const [mode, setMode] = useState<ThemeMode>('auto');

  useQueueTriggers();

  useEffect(() => {
    void loadThemeMode().then((stored) => {
      setMode(stored);
      applyThemeMode(stored);
    });
  }, []);

  if (state.status === 'loading') {
    return (
      <div className="wrap" style={{ paddingTop: '25vh' }}>
        <div className="empty">
          <span className="spinner" style={{ margin: '0 auto' }} />
          <p style={{ marginTop: 'var(--space-3)' }}>กำลังเข้าสู่ระบบ…</p>
        </div>
      </div>
    );
  }

  /*
   * ยังไม่ได้ล็อกอิน -> แสดงหน้าเข้าสู่ระบบ
   *
   * ต่างจากตอนใช้ Cloudflare Access ที่ผู้ใช้ถูกพาไปหน้าล็อกอินของ Cloudflare
   * ตั้งแต่ก่อนถึงแอป ตอนนี้แอปเป็นผู้รับผิดชอบหน้าล็อกอินเอง
   */
  if (state.status === 'unauthenticated') {
    return <LoginRoute onSignedIn={reload} />;
  }

  if (state.status !== 'ready') {
    return <AccessScreen state={state} onRetry={reload} />;
  }

  /*
   * ล็อกอินแล้วแต่ยังใช้รหัสชั่วคราวอยู่ -> บังคับตั้งรหัสใหม่ก่อน
   * server กั้น endpoint อื่นไว้ด้วยอยู่แล้ว ตรงนี้ทำให้ผู้ใช้เห็นว่าต้องทำอะไร
   * แทนที่จะเจอ error 403 ทุกหน้า
   */
  if (state.session.mustChangePassword) {
    return (
      <ChangePasswordRoute
        forced
        onChanged={reload}
        minPasswordLength={state.session.settings.auth.minPasswordLength}
        email={state.session.user.email}
        displayName={state.session.user.displayName}
      />
    );
  }

  return (
    <BrowserRouter>
      <AppShell
        session={state.session}
        mode={mode}
        onModeChange={(next) => {
          setMode(next);
          applyThemeMode(next);
        }}
      >
        <Routes>
          <Route path="/" element={<Navigate to="/capture" replace />} />
          <Route path="/capture" element={<CaptureRoute session={state.session} />} />
          <Route path="/result/:queueId" element={<ResultRoute />} />
          <Route path="/my" element={<MyListRoute userId={state.session.user.id} />} />
          <Route path="/captures/:id" element={<DetailRoute session={state.session} />} />
          <Route
            path="/search"
            element={
              <RequireRole role="supervisor" user={state.session.user}>
                <SearchRoute session={state.session} />
              </RequireRole>
            }
          />
          <Route
            path="/dashboard"
            element={
              <RequireRole role="supervisor" user={state.session.user}>
                <DashboardRoute />
              </RequireRole>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireRole role="administrator" user={state.session.user}>
                <AdminRoute />
              </RequireRole>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RequireRole role="administrator" user={state.session.user}>
                <AdminUsersRoute />
              </RequireRole>
            }
          />
          <Route
            path="/account/password"
            element={
              <ChangePasswordRoute
                forced={false}
                onChanged={reload}
                minPasswordLength={state.session.settings.auth.minPasswordLength}
                email={state.session.user.email}
                displayName={state.session.user.displayName}
              />
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AppShell>
    </BrowserRouter>
  );
}

/**
 * ซ่อนหน้าที่ผู้ใช้ไม่มีสิทธิ์
 * เป็นเพียงการจัดระเบียบ UI เท่านั้น การควบคุมสิทธิ์จริงอยู่ที่ server ทุก endpoint
 */
function RequireRole({
  role,
  user,
  children,
}: {
  role: Role;
  user: SessionResponse['user'];
  children: React.ReactNode;
}) {
  if (ROLE_RANK[user.role] < ROLE_RANK[role]) {
    return (
      <div className="wrap">
        <div className="banner banner-bad" role="alert">
          <span>บัญชีของคุณไม่มีสิทธิ์เข้าถึงหน้านี้</span>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

function NotFound() {
  return (
    <div className="wrap">
      <div className="empty">ไม่พบหน้าที่ต้องการ</div>
    </div>
  );
}

/** หน้าจอตอนยังไม่ได้เข้าสู่ระบบ ถูกปฏิเสธสิทธิ์ หรือเชื่อมต่อไม่ได้ */
function AccessScreen({
  state,
  onRetry,
}: {
  state: { status: 'unauthenticated' | 'forbidden' | 'error'; message: string };
  onRetry: () => void;
}) {
  const titles = {
    unauthenticated: 'กรุณาเข้าสู่ระบบ',
    forbidden: 'บัญชีนี้ยังไม่มีสิทธิ์ใช้งาน',
    error: 'เชื่อมต่อระบบไม่ได้',
  } as const;

  return (
    <div className="wrap stack-lg" style={{ paddingTop: '15vh' }}>
      <div className="card stack">
        <h1 className="card-title">{titles[state.status]}</h1>
        <p className="card-sub">{state.message}</p>
        <div className="perf" />
        {state.status === 'forbidden' && (
          <p className="small muted">
            ตัวตนของคุณถูกยืนยันแล้ว แต่ยังไม่ได้รับสิทธิ์ในระบบนี้
            กรุณาติดต่อผู้ดูแลระบบเพื่อขอเพิ่มบัญชี
          </p>
        )}
        <button type="button" className="btn btn-block" onClick={onRetry}>
          ลองใหม่
        </button>
      </div>
    </div>
  );
}
